import {
  DROPDOWN_OPTIONS,
  LIEN_LIST_DATA,
} from '@pages/DocumentAuditPage/modules/DocumentTaskDetail/components/DocumentReview/DocumentForms/ApproveForm/constants';
import { CountryCode } from '@domains/country';

/**
 * Campos MX excluidos del flujo de sugerencias/autocompletado.
 * El formulario manual sigue activo. Para reactivar: vaciar este array.
 */
export const DISABLED_SUGGESTION_FIELDS_MX: readonly string[] = ['accessoryAreas'];

export const isDisabledSuggestionField = (
  field: string,
  country?: CountryCode
): boolean =>
  country === CountryCode.MX && DISABLED_SUGGESTION_FIELDS_MX.includes(field);

export type FieldType =
  | 'text'
  | 'number'
  | 'date'
  | 'price'
  | 'select'
  | 'multiselect';

export interface FieldConfig {
  type: FieldType;
  options?: { value: string | number; label: string }[];
  min?: number;
  max?: number;
  maxLength?: number;
  placeholder?: string;
}

// Configuración de campos para Colombia (CO)
export const FIELD_CONFIGS_CO: Record<string, FieldConfig> = {
  // Dropdown fields
  hasAdmin: {
    type: 'select',
    options: DROPDOWN_OPTIONS.YES_NO,
  },
  areaType: {
    type: 'select',
    options: DROPDOWN_OPTIONS.AREA_TYPE,
  },
  hasGarage: {
    type: 'select',
    options: DROPDOWN_OPTIONS.GARAGE,
  },
  hasTerrace: {
    type: 'select',
    options: DROPDOWN_OPTIONS.YES_NO,
  },
  lenderBank: {
    type: 'select',
    options: DROPDOWN_OPTIONS.BANK_LIST,
  },
  liens: {
    type: 'multiselect',
    options: LIEN_LIST_DATA.map(([label, value]) => ({ label, value })),
  },
  liensCategory: {
    type: 'select',
    options: DROPDOWN_OPTIONS.LIEN_CATEGORY,
  },
  propertyType: {
    type: 'select',
    options: DROPDOWN_OPTIONS.PROPERTY_TYPE_CO,
  },
  elevators: {
    type: 'number',
    min: 0,
    max: 20,
    placeholder: 'Elevadores',
  },

  // Price fields
  adminPrice: {
    type: 'price',
    placeholder: 'Valor administración',
  },

  // Date fields
  constructionDate: {
    type: 'date',
  },

  // Number fields
  baths: {
    type: 'number',
    min: 0,
    max: 20,
    placeholder: 'Baños',
  },
  floor: {
    type: 'number',
    min: 0,
    max: 20,
    placeholder: 'Piso',
  },
  garage: {
    type: 'number',
    min: 0,
    max: 20,
    placeholder: 'Cantidad de garajes',
  },

  // Text/number hybrid fields (area, freeArea)
  area: {
    type: 'text',
    maxLength: 3,
    placeholder: 'Área (m²)',
  },
  freeArea: {
    type: 'text',
    maxLength: 3,
    placeholder: 'Área libre (m²)',
  },
};

// Configuración de campos para México (MX)
export const FIELD_CONFIGS_MX: Record<string, FieldConfig> = {
  // Dropdown fields
  propertyType: {
    type: 'select',
    options: DROPDOWN_OPTIONS.PROPERTY_TYPE,
  },
  documentType: {
    type: 'select',
    options: DROPDOWN_OPTIONS.PROPERTY_DOCUMENT_TYPE_OPTIONS,
  },
  hasGarageDoc: {
    type: 'select',
    options: DROPDOWN_OPTIONS.GARAGE,
  },
  parkingWithDoubleKeyOrDeed: {
    type: 'select',
    options: DROPDOWN_OPTIONS.YES_NO,
  },

  // Text fields
  complex: {
    type: 'text',
    placeholder: 'Nombre del conjunto',
  },
  medianZone: {
    type: 'text',
    placeholder: 'Zona mediana',
  },
  zipCode: {
    type: 'text',
    placeholder: 'Código postal',
  },

  // Price fields
  adminPrice: {
    type: 'price',
    placeholder: 'Valor administración',
  },

  // Number fields
  committeePriceArea: {
    type: 'number',
    placeholder: 'Área construcción',
  },
  committeeLandArea: {
    type: 'number',
    placeholder: 'Área terreno',
  },
  committeeExpansionArea: {
    type: 'number',
    placeholder: 'Área ampliación',
  },
  rooms: {
    type: 'number',
    min: 0,
    max: 20,
    placeholder: 'Recámaras',
  },
  baths: {
    type: 'number',
    min: 0,
    max: 20,
    placeholder: 'Baños',
  },
  halfBathroom: {
    type: 'number',
    min: 0,
    max: 20,
    placeholder: 'Medios baños',
  },
  floor: {
    type: 'number',
    min: 0,
    max: 20,
    placeholder: 'Piso',
  },
  elevator: {
    type: 'number',
    min: 0,
    max: 1,
    placeholder: 'Elevador',
  },
  garage: {
    type: 'number',
    min: 0,
    max: 20,
    placeholder: 'Estacionamientos',
  },
  yearsOld: {
    type: 'number',
    min: 0,
    max: 200,
    placeholder: 'Antigüedad',
  },
  accessoryAreas: {
    type: 'text',
    placeholder: 'kind,meters,roofed',
  },
};

/**
 * Mapeo de nombres de campos entre suggestions (backend) y formulario (frontend)
 * Ejemplo: halfBathroom (suggestions) → halfBaths (formulario)
 */
const FIELD_NAME_MAPPING: Record<string, string> = {
  halfBathroom: 'halfBaths', // MX: medios baños
};

/**
 * Convierte nombre de campo desde suggestions al nombre usado en el formulario
 */
export const mapSuggestionFieldToFormField = (field: string): string => {
  return FIELD_NAME_MAPPING[field] || field;
};

/**
 * Convierte nombre de campo del formulario al nombre usado en suggestions
 */
export const mapFormFieldToSuggestionField = (field: string): string => {
  const reverseMapping = Object.entries(FIELD_NAME_MAPPING).find(
    ([, formField]) => formField === field
  );
  return reverseMapping ? reverseMapping[0] : field;
};

/**
 * Obtiene la configuración de un campo basado en el país
 */
export const getFieldConfig = (field: string, country: string): FieldConfig => {
  const configs = country === 'CO' ? FIELD_CONFIGS_CO : FIELD_CONFIGS_MX;
  return (
    configs[field] || {
      type: 'text',
      placeholder: 'Ingrese el valor',
    }
  );
};
